import { z } from 'zod';

import { assertUnreachable } from './assert.js';

declare const percentBrand: unique symbol;

/**
 * A percentage as its whole-percent number (e.g. `42.5` for 42.5%), the wire
 * form the backend serializes as a decimal string. Compare with `===`, and
 * render/convert through the `Percent` namespace rather than the raw number.
 */
export type Percent = number & { readonly [percentBrand]: 'Percent' };

/** The only place a `number` becomes a `Percent`; the value is whole percent. */
const brand = (wholePercent: number): Percent => wholePercent as Percent;

export const Percent = {
  /** Brand a whole-percent number (e.g. `10` for 10%). */
  fromNumber: (wholePercent: number): Percent => brand(wholePercent),

  /** Parse the decimal-string wire form (e.g. `"42.5"`). */
  fromString: (value: string): Percent => brand(parseFloat(value)),

  /** Build from a fraction (e.g. `0.425` → 42.5%). */
  fromFraction: (fraction: number): Percent => brand(100 * fraction),

  /** The fraction form (e.g. 42.5% → `0.425`). */
  fraction: (percent: Percent): number => percent / 100,

  display: (
    percent: Percent,
    fractionDigits: number = 2,
    format: 'se' | 'global' = 'se',
    end: '' | '%' | ' %' = '%',
  ): string => {
    const fixed = percent.toFixed(fractionDigits);
    switch (format) {
      case 'se':
        return fixed.replace('.', ',') + end;
      case 'global':
        return fixed + end;
      default:
        assertUnreachable(format);
    }
  },

  /** Zod schema for the decimal-string wire form; parses and brands. */
  schema: z.string().transform((value): Percent => brand(parseFloat(value))),
};
